import { Settings as SettingsIcon, Moon, Sun, Monitor, User, Info } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import { useTheme } from '../hooks/useTheme';
import { cn, formatDateTime } from '../lib/utils';

export function Settings() {
  const { theme, setTheme } = useTheme();

  const themeOptions = [
    { value: 'dark' as const, label: 'Dark', icon: Moon, description: 'Optimized for low-light environments' },
    { value: 'light' as const, label: 'Light', icon: Sun, description: 'Standard light theme' },
  ];

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-semibold text-text-primary flex items-center gap-2">
          <SettingsIcon className="w-6 h-6 text-accent-blue" />
          Settings
        </h1>
        <p className="text-text-secondary mt-1">
          Configure your analyst portal preferences
        </p>
      </div>

      {/* Appearance */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Monitor className="w-5 h-5 text-accent-blue" />
            Appearance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <label className="text-sm text-text-secondary">Theme</label>
            <div className="grid grid-cols-2 gap-4">
              {themeOptions.map((option) => {
                const Icon = option.icon;
                const isSelected = theme === option.value;

                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setTheme(option.value)}
                    className={cn(
                      'flex items-start gap-3 p-4 rounded-lg border text-left transition-all',
                      isSelected
                        ? 'bg-accent-blue/10 border-accent-blue'
                        : 'bg-bg-secondary border-border-default hover:border-accent-blue/50'
                    )}
                  >
                    <Icon className={cn('w-5 h-5 mt-0.5', isSelected ? 'text-accent-blue' : 'text-text-secondary')} />
                    <div>
                      <div className={cn('font-medium', isSelected ? 'text-accent-blue' : 'text-text-primary')}>
                        {option.label}
                      </div>
                      <div className="text-xs text-text-secondary mt-0.5">
                        {option.description}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Session info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5 text-accent-blue" />
            Session
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between py-2 border-b border-border-default">
            <span className="text-sm text-text-secondary">User</span>
            <span className="text-sm text-text-primary font-mono">analyst@alexein.io</span>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-border-default">
            <span className="text-sm text-text-secondary">Role</span>
            <span className="text-sm text-text-primary">SOC Analyst</span>
          </div>
          <div className="flex items-center justify-between py-2">
            <span className="text-sm text-text-secondary">Session Started</span>
            <span className="text-sm text-text-primary font-mono">
              {formatDateTime(new Date().toISOString())}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* About */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Info className="w-5 h-5 text-accent-blue" />
            About
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between py-2 border-b border-border-default">
            <span className="text-sm text-text-secondary">Application</span>
            <span className="text-sm text-text-primary">Alexein Analyst Portal</span>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-border-default">
            <span className="text-sm text-text-secondary">Version</span>
            <span className="text-sm text-text-primary font-mono">0.1.0</span>
          </div>
          <div className="flex items-center justify-between py-2">
            <span className="text-sm text-text-secondary">Environment</span>
            <span className="text-sm text-text-primary font-mono">Development</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
